import React, { useState } from "react";
import { TrustPill } from "../TrustPill";
import type { ExecutiveSiteRow } from "../../lib/executive-seo-types";

type Watchlists = {
  businessSignal: ExecutiveSiteRow[];
  visibilityNoClicks: ExecutiveSiteRow[];
  organicTrafficNoGscClicks: ExecutiveSiteRow[];
  rankMovementNoGscCorroboration: ExecutiveSiteRow[];
  indexationLag: ExecutiveSiteRow[];
  auditIssues: ExecutiveSiteRow[];
  staleData: ExecutiveSiteRow[];
  twilioProvisioningCandidates: ExecutiveSiteRow[];
  notActionableYet: ExecutiveSiteRow[];
};

type Key = keyof Watchlists;

const TABS: Array<{ key: Key; label: string; help: string }> = [
  { key: "businessSignal", label: "Real business signal", help: "Sites with ≥1 non-test lead in 30d." },
  { key: "visibilityNoClicks", label: "SEO visibility, no clicks", help: "≥100 GSC impressions in 30d but zero clicks." },
  { key: "organicTrafficNoGscClicks", label: "Organic traffic, no GSC clicks", help: "GA4 organic sessions present but GSC clicks zero — attribution caveat." },
  { key: "rankMovementNoGscCorroboration", label: "Rank move, no GSC", help: "DataForSEO rank change without GSC corroboration." },
  { key: "indexationLag", label: "Indexation lag", help: "<25% of inspected URLs are 'Submitted and indexed'." },
  { key: "auditIssues", label: "Audit warns/errors", help: "Latest audit_runs reports warn or fail." },
  { key: "staleData", label: "Stale data", help: "Sites whose canonical surfaces have aged past the freshness window." },
  { key: "twilioProvisioningCandidates", label: "Twilio candidates", help: "CENTRAL_OPERATOR sites that satisfy at least one promotion trigger." },
  { key: "notActionableYet", label: "Not actionable yet", help: "Posts=0 and impressions<100 — explicit park." },
];

export function WatchlistPanel({ watchlists }: { watchlists: Watchlists }): React.ReactElement {
  const [tab, setTab] = useState<Key>("businessSignal");
  const rows = watchlists[tab];

  return (
    <div>
      <div className="flex flex-wrap gap-1 mb-3 border-b border-ink-100">
        {TABS.map((t) => {
          const n = watchlists[t.key].length;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${active ? "border-ink-900 text-ink-900 font-medium" : "border-transparent text-ink-600 hover:text-ink-800"}`}
            >
              {t.label} <span className="text-ink-400 text-xs">({n})</span>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-ink-500 mb-3">{TABS.find((t) => t.key === tab)?.help}</p>

      {rows.length === 0 ? (
        <div className="text-sm text-ink-500 italic bg-ink-50 rounded-lg px-4 py-6 text-center border border-ink-100">
          {tab === "businessSignal"
            ? "No real homeowner lead signal has been confirmed in this window."
            : `No sites match this watchlist right now.`}
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl border border-ink-100 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-ink-50">
              <tr className="text-xs uppercase tracking-wider text-ink-600">
                <th className="text-left px-3 py-2">domain</th>
                <th className="text-left px-3 py-2">niche / city</th>
                <th className="text-right px-3 py-2">leads</th>
                <th className="text-right px-3 py-2">GSC imp</th>
                <th className="text-right px-3 py-2">organic</th>
                <th className="text-right px-3 py-2">desktop t30</th>
                <th className="text-left px-3 py-2">trust</th>
                <th className="text-left px-3 py-2">note</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-ink-100">
                  <td className="px-3 py-2 font-mono text-xs">{r.domain}</td>
                  <td className="px-3 py-2 text-xs">{r.niche} · {r.city}, {r.state}</td>
                  <td className={`px-3 py-2 text-right ${r.realLeads30d > 0 ? "text-emerald-700 font-medium" : ""}`}>{r.realLeads30d}</td>
                  <td className="px-3 py-2 text-right">{r.gscImpressions30d.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{r.organicSessions30d}</td>
                  <td className="px-3 py-2 text-right">{r.desktopTop30}</td>
                  <td className="px-3 py-2"><TrustPill label={r.trustLabel} /></td>
                  <td className="px-3 py-2 text-xs text-ink-700">{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
